/*
 * Copyright 2021, IntraLinks, Inc. All rights reserved.
 */
import forge from 'node-forge';
import Cookies from 'js-cookie';
import { useLocation } from 'react-router';
import { getHttpErrorStatusCode } from './errors.util';
import {
  DEFAULT_EMAIL_MASK_KEEP,
  DEFAULT_EMAIL_MASK_STARS,
  DEFAULT_MIN_REQUIRED_LOA_LEVEL,
  DEFAULT_SECRET_KEY_LENGTH,
  ValidationErrorKey
} from './constants.util';

export const checkComplexity = (password: string): { score: number; repeatedCharacters: boolean } => {
  // TODO: check for forbidden chars?
  // TODO: check against leaked passwords
  let score = password !== '' ? 1 : 0;
  let repeatedCharacters = false;
  if (password.length >= 1 && password.length <= 5) {
    score = 1;
  }
  if (password.length >= 6 && password.length <= 9) {
    score = 2;
  }
  if (password.length >= 10 && password.length <= 12) {
    score = 3;
  }
  if (password.length > 12) {
    score = 4;
  }
  if (password.match(/((.)\2{2,})/)) {
    repeatedCharacters = true;

    if (score > 2) {
      score = 2;
    }
  }
  return { score, repeatedCharacters };
};

export const fetchExpiryDate = (certificate: string) => {
  const getCertificate = (certificate.toUpperCase().includes('BEGIN') && certificate.toUpperCase().includes('END'))
    ? certificate : `"-----BEGIN CERTIFICATE-----${certificate}-----END CERTIFICATE-----"`;
  const certificateExpDate = forge.pki.certificateFromPem(getCertificate);
  return certificateExpDate.validity.notAfter.toISOString();
};

export const setLocaleCookie = (languageCode: string): void => {
  let lang = '';
  switch (languageCode) {
    case 'en-US':
      lang = 'en_us';
      break;
    case 'en-GB':
      lang = 'gb';
      break;
    default:
      lang = languageCode.substr(0, 2);
  }
  Cookies.set('locale', lang, { domain: 'intralinks.com', expires: 3650 }); // 10y
};

export function useRequiredLevel(defaultLevel = DEFAULT_MIN_REQUIRED_LOA_LEVEL, min = DEFAULT_MIN_REQUIRED_LOA_LEVEL): number {
  const { search } = useLocation();
  const v = new URLSearchParams(search).get('loaLevel');
  let n = Number(v);

  if (!Number.isFinite(n)) return defaultLevel;
  n = Math.floor(n);
  return n >= min ? n : defaultLevel;
}

export function getSecretKeyLengthFromUri(uri: string): number {
  const url = new URL(uri);
  return parseInt(url.searchParams.get('digits') ?? DEFAULT_SECRET_KEY_LENGTH.toString(), 10);
}

export function toReasonString(err: unknown): string {
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message || err.name;

  const msg = (err as any)?.response?.data?.message
    ?? (err as any)?.message
    ?? (err as any)?.error
    ?? '';

  const status = getHttpErrorStatusCode(err);
  const bits = [];
  if (status) bits.push(`status ${status}`);
  if (msg) bits.push(String(msg));

  return bits.join(': ') || 'Unknown error';
}

export function capitalizeFirstLetter(text: string): string {
  if (!text) return text;

  return text
    .split(' ')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(' ');
}

export function maskEmailFixed(
  email: string,
  { keep = DEFAULT_EMAIL_MASK_KEEP, stars = DEFAULT_EMAIL_MASK_STARS }: { keep?: number; stars?: number } = {}
): string {
  const e = (email ?? '').trim();
  const at = e.indexOf('@');
  if (at <= 0 || at === e.length - 1) return e;

  const local = e.slice(0, at);
  const domain = e.slice(at + 1);

  const prefix = local.slice(0, Math.min(keep, local.length));
  return `${prefix}${'*'.repeat(stars)}@${domain}`;
}

export function getLoaValidationMessageKey(err?: string): string | null {
  switch (err) {
    case ValidationErrorKey.LabelRequired: return 'loaEnrichment.common.validation.labelRequired';
    case ValidationErrorKey.LabelTooLong: return 'loaEnrichment.common.validation.labelTooLong';
    case ValidationErrorKey.LabelForbiddenChars: return 'loaEnrichment.common.validation.labelForbiddenChars';
    case ValidationErrorKey.SmsInvalidPhone: return 'loaEnrichment.common.validation.invalidPhone';
    case ValidationErrorKey.InvalidSMSCode:
    case ValidationErrorKey.InvalidEmailCode:
    case ValidationErrorKey.OtpInvalid: return 'loaEnrichment.common.errors.otpInvalid';
    default: return null;
  }
}
